'use client';

import Link from 'next/link';
import { MouseEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  BookOpen,
  Clock3,
  Compass,
  FileText,
  LayoutDashboard,
  MoreHorizontal,
  Scale,
  Search,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import { AppBreadcrumbs } from '@/components/mui/app-breadcrumbs';
import { MobileNav } from '@/components/mobile-nav';
import { BrandLogo } from '@/components/navbar/brand-logo';
import { estimateVisibleGroupCount } from '@/components/navbar/progressive-navigation';
import { InstrumentSearch } from '@/components/navbar/ticker-search';
import { ThemeToggle } from '@/components/theme-toggle';
import { UserDropdown } from '@/components/user-dropdown';
import { authConfig } from '@/config/auth-config';
import {
  authenticatedNavSections,
  authenticatedNavGroups,
  filterG2rsModeGroups,
  filterG2rsModeSections,
  publicNavGroups,
  publicNavSections,
  resolveG2rsModeNavLink,
  type ObjectiveNavGroup,
  type ObjectiveNavLink,
  type ObjectiveNavSection,
} from '@/config/navigation-links-config';
import { NON_G2RS_FEATURE_FLAG } from '@/config/non-g2rs-mode-config';
import { useAuth } from '@/hooks/use-auth';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { COURSE_FEATURE_FLAG } from '@/libs/course/course-routes';

type ResolvedNavLink = {
  title: string;
  url: string;
  description?: string;
  soon?: boolean;
};

type ResolvedNavGroup = {
  title: string;
  icon: LucideIcon;
  links: ResolvedNavLink[];
};

const navigationIcons: Record<NonNullable<ObjectiveNavLink['navigationIcon']>, LucideIcon> = {
  dashboard: LayoutDashboard,
  portfolio: BarChart3,
  funds: Search,
  analysis: Scale,
  copilot: Sparkles,
  reports: FileText,
  knowledge: BookOpen,
  explore: Compass,
  more: MoreHorizontal,
};

const NavigationLinkLabel = ({ link, wrap = false }: { link: ResolvedNavLink; wrap?: boolean }) => (
  <Box
    component='span'
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.3,
      whiteSpace: wrap ? 'normal' : 'nowrap',
    }}
  >
    {link.title}
    {link.soon ? (
      <Box
        component='span'
        title='Em breve'
        aria-label='Em breve'
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          color: 'text.secondary',
          opacity: 0.8,
        }}
      >
        <Clock3 size={12} strokeWidth={2.2} aria-hidden='true' />
      </Box>
    ) : null}
  </Box>
);

// Keep every group heading on the same top baseline when a group's links wrap
// to a second row, while centering the complete navigation block vertically.
export const navigationGroupLayout = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
} as const;

// The shared height keeps one-row groups aligned with groups whose links wrap.
export const navigationGroupContentLayout = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  minHeight: 56,
} as const;

export const navigationGroupTitleLayout = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  mb: 0.55,
  color: 'text.secondary',
  opacity: 0.72,
} as const;

export const navigationLinkMinHeight = 20;

// Center the complete menu only while it fits. Once the max-content width is
// larger than the viewport, flex centering naturally falls back to the first
// group so progressive overflow keeps its left-to-right order.
export const navigationMenuContentLayout = {
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'center',
  width: 'max-content',
  minWidth: '100%',
  minHeight: 60,
} as const;

export const getNavigationLinkGridLayout = (linkCount: number) => {
  const usesTwoColumns = linkCount > 2;

  return {
    gridTemplateColumns: usesTwoColumns ? 'repeat(2, max-content)' : 'max-content',
    gridTemplateRows: usesTwoColumns ? 'repeat(2, minmax(0, auto))' : undefined,
    gridAutoFlow: usesTwoColumns ? 'column' : 'row',
  } as const;
};

// Two compact link rows plus the group heading fit comfortably in 64px.
export const desktopToolbarHeight = 64;

export const resolveCourseNavigationLink = <T extends { title: string; url: string; soon?: boolean }>(
  link: T,
  courseEnabled: boolean,
): T => {
  if (link.url !== '/landing/curso') return link;
  return { ...link, url: courseEnabled ? '/cursos' : '/landing/curso', soon: courseEnabled ? undefined : true };
};

/**
 * The shell and product navigation are shared between visitors and signed-in
 * users. Each destination owns the behavior for visitors without a session.
 */
export const AppNavbar = ({
  initialIsAuthenticated = false,
  initialCourseEnabled = false,
  initialG2rsMode = false,
}: {
  initialIsAuthenticated?: boolean;
  initialCourseEnabled?: boolean;
  initialG2rsMode?: boolean;
}) => {
  const { status } = useAuth();
  const { isEnabled, ready } = useFeatureFlags();
  const isAuthenticated = status === 'authenticated' || (status === 'loading' && initialIsAuthenticated);
  const courseEnabled = ready ? isEnabled(COURSE_FEATURE_FLAG) : initialCourseEnabled;
  const g2rsMode = ready ? !isEnabled(NON_G2RS_FEATURE_FLAG) : initialG2rsMode;
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const sectionSource = filterG2rsModeSections(
    isAuthenticated ? authenticatedNavSections : publicNavSections,
    g2rsMode,
  );
  const groupSource = filterG2rsModeGroups(isAuthenticated ? authenticatedNavGroups : publicNavGroups, g2rsMode);

  const filteredSections = sectionSource
    .map<ObjectiveNavSection>((section) => ({
      ...section,
      links: section.links
        .filter((link) => isEnabled(link.featureFlag))
        .map((link) => ({
          ...link,
          ...resolveCourseNavigationLink(link, courseEnabled),
          ...resolveG2rsModeNavLink(link, g2rsMode),
        })),
    }))
    .filter((section) => section.links.length > 0);

  const navigationGroups: ResolvedNavGroup[] = useMemo(
    () =>
      groupSource
        .map((group: ObjectiveNavGroup) => ({
          title: group.title,
          icon: navigationIcons[group.navigationIcon],
          links: group.links
            .filter((link) => isEnabled(link.featureFlag))
            .map((link) => {
              const resolvedLink = {
                ...resolveCourseNavigationLink(link, courseEnabled),
                ...resolveG2rsModeNavLink(link, g2rsMode),
              };
              return {
                ...resolvedLink,
                title: link.compactTitle ?? resolvedLink.title,
                description: link.description,
              };
            }),
        }))
        .filter((group) => group.links.length > 0),
    [courseEnabled, groupSource, isAuthenticated, isEnabled, g2rsMode],
  );

  const groupViewportRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<Array<HTMLDivElement | null>>([]);
  const moreButtonRef = useRef<HTMLDivElement>(null);
  const groupWidthsRef = useRef<number[]>([]);
  const [visibleGroupCount, setVisibleGroupCount] = useState(navigationGroups.length);
  const groupSignature = navigationGroups
    .map((group) => `${group.title}:${group.links.map((link) => link.title)}`)
    .join('|');
  const secondarySignature = filteredSections
    .map((section) => `${section.title}:${section.links.map((link) => link.title)}`)
    .join('|');

  useLayoutEffect(() => {
    const viewport = groupViewportRef.current;
    if (!viewport) return;

    groupWidthsRef.current = [];
    setVisibleGroupCount(navigationGroups.length);

    const measure = () => {
      if (viewport.clientWidth === 0) return;

      const groupWidths = navigationGroups.map((_, index) => {
        const measuredWidth = groupRefs.current[index]?.getBoundingClientRect().width ?? 0;
        if (measuredWidth > 0) {
          groupWidthsRef.current[index] = measuredWidth;
        }
        return groupWidthsRef.current[index] ?? measuredWidth;
      });
      const moreWidth = moreButtonRef.current?.getBoundingClientRect().width ?? 0;
      const nextVisibleGroupCount = estimateVisibleGroupCount(viewport.clientWidth, groupWidths, moreWidth);

      setVisibleGroupCount((current) => (current === nextVisibleGroupCount ? current : nextVisibleGroupCount));
    };

    const frame = window.requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    groupRefs.current.forEach((group) => {
      if (group) observer.observe(group);
    });
    if (moreButtonRef.current) observer.observe(moreButtonRef.current);

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [groupSignature, navigationGroups.length, secondarySignature]);

  const mobileSections = [
    ...navigationGroups.map((group) => ({
      title: group.title,
      links: group.links,
    })),
    ...filteredSections,
  ];

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const overflowGroups = navigationGroups.slice(visibleGroupCount);

  return (
    <AppBar
      position='sticky'
      elevation={0}
      color='transparent'
      sx={{
        top: 0,
        borderBottom: '1px solid hsl(var(--border))',
        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'hsl(var(--nav-bg))' : 'hsl(var(--nav-bg))'),
        backgroundImage: 'none',
        color: (theme) => (theme.palette.mode === 'dark' ? 'hsl(var(--nav-foreground))' : 'hsl(var(--foreground))'),
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 8px 20px hsl(var(--foreground) / 0.28)'
            : '0 6px 14px hsl(var(--foreground) / 0.08)',
      }}
    >
      <Container maxWidth='xl' sx={{ px: { xs: 2, md: 3 } }}>
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 112, md: desktopToolbarHeight },
            height: { xs: 112, md: desktopToolbarHeight },
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr) auto',
            gridTemplateRows: {
              xs: '60px 52px',
              md: `${desktopToolbarHeight}px`,
            },
            alignItems: 'center',
            columnGap: { xs: 1, md: 1, lg: 1.25 },
            rowGap: 0,
          }}
          aria-label='main navbar'
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gridColumn: 1,
              gridRow: 1,
              alignSelf: 'center',
              pr: { md: 0.5, xl: 1 },
            }}
          >
            <BrandLogo href='/' />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: { xs: 0.5, md: 1 },
              gridColumn: 3,
              gridRow: 1,
              alignSelf: 'center',
              pl: { md: 0.5, xl: 1 },
              minWidth: 0,
            }}
          >
            <ThemeToggle />
            {status === 'loading' && <CircularProgress size={18} />}
            {status === 'authenticated' && <UserDropdown />}
            {status === 'unauthenticated' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  component={Link}
                  href={authConfig.loginUrl}
                  variant='text'
                  color='inherit'
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  Entrar
                </Button>
                <Button
                  component={Link}
                  href={authConfig.loginUrlForRedirect('/carteira/nova')}
                  variant='contained'
                  sx={{
                    px: { xs: 1, sm: 2 },
                    fontSize: { xs: 11, sm: '0.875rem' },
                    textTransform: 'none',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Começar grátis
                </Button>
              </Box>
            )}
          </Box>
          <Box
            component='nav'
            aria-label='Navegação principal'
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'auto minmax(0, 1fr)',
                md: 'minmax(0, 1fr) auto',
              },
              gridColumn: { xs: '1 / -1', md: 2 },
              gridRow: { xs: 2, md: 1 },
              alignSelf: 'stretch',
              width: '100%',
              minWidth: 0,
              borderTop: {
                xs: '1px solid hsl(var(--border) / 0.7)',
                md: 'none',
              },
              borderBottom: '1px solid hsl(var(--border) / 0.7)',
              backgroundColor: 'hsl(var(--background) / 0.18)',
            }}
          >
            <Box sx={{ display: { xs: 'flex', md: 'none' }, minWidth: 0 }}>
              <MobileNav links={mobileSections} desktopBreakpoint='md' />
            </Box>
            <Box
              ref={groupViewportRef}
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'stretch',
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Box sx={navigationMenuContentLayout}>
                {navigationGroups.map((group, index) => {
                  const GroupIcon = group.icon;

                  return (
                    <Box
                      key={group.title}
                      ref={(node: HTMLDivElement | null) => {
                        groupRefs.current[index] = node;
                      }}
                      sx={{
                        ...navigationGroupLayout,
                        display: index < visibleGroupCount ? 'flex' : 'none',
                        minWidth: 0,
                        flexShrink: 0,
                        px: { md: 0.65, xl: 0.85 },
                        py: 0.25,
                        borderLeft: index === 0 ? 'none' : '1px solid hsl(var(--border) / 0.72)',
                      }}
                    >
                      <Box sx={navigationGroupContentLayout}>
                        <Box sx={navigationGroupTitleLayout}>
                          <GroupIcon size={12} strokeWidth={2.2} aria-hidden='true' />
                          <Typography
                            component='span'
                            sx={{
                              fontSize: 9,
                              lineHeight: 1.2,
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase',
                              fontWeight: 750,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {group.title}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'grid',
                            ...getNavigationLinkGridLayout(group.links.length),
                            alignItems: 'center',
                            columnGap: { md: 0.35, xl: 0.5 },
                            rowGap: 0,
                          }}
                        >
                          {group.links.map((link) => (
                            <Button
                              key={link.title}
                              component={Link}
                              href={link.url}
                              variant='text'
                              color='inherit'
                              aria-label={`Ir para ${link.title}${link.soon ? ' (em breve)' : ''}`}
                              sx={{
                                minHeight: navigationLinkMinHeight,
                                textTransform: 'none',
                                fontWeight: 650,
                                fontSize: 11.5,
                                lineHeight: 1.1,
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                px: 0.2,
                                py: 0.1,
                                borderRadius: 1,
                                whiteSpace: 'nowrap',
                                transition: 'background-color 0.18s ease, color 0.18s ease',
                                '&:hover': {
                                  backgroundColor: 'hsl(var(--primary) / 0.18)',
                                  color: 'hsl(var(--primary))',
                                },
                              }}
                            >
                              <NavigationLinkLabel link={link} />
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                {filteredSections.length > 0 ? (
                  <Box
                    key='more-menu'
                    ref={moreButtonRef}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 0,
                      flexShrink: 0,
                      px: { md: 0, xl: 0.25 },
                      borderLeft: '1px solid hsl(var(--border) / 0.72)',
                    }}
                  >
                    <Button
                      variant='text'
                      color='inherit'
                      aria-haspopup='menu'
                      aria-expanded={Boolean(menuAnchorEl)}
                      aria-label='Abrir Mais'
                      title='Mais opções'
                      sx={{
                        minWidth: 32,
                        minHeight: 27,
                        fontWeight: 850,
                        fontSize: 10,
                        letterSpacing: '0.09em',
                        textTransform: 'uppercase',
                        px: 0.35,
                        py: 0.25,
                        borderRadius: 1,
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          backgroundColor: 'hsl(var(--primary) / 0.18)',
                          color: 'hsl(var(--primary))',
                        },
                        '&:focus-visible': {
                          outline: '2px solid hsl(var(--ring))',
                          outlineOffset: 2,
                        },
                        transition: 'background-color 0.18s ease, color 0.18s ease',
                        '& > svg:last-of-type': {
                          ml: 0.15,
                          transition: 'transform 0.18s ease',
                          transform: menuAnchorEl ? 'rotate(180deg)' : 'none',
                        },
                      }}
                      onClick={openMenu}
                    >
                      <MoreHorizontal size={15} strokeWidth={2.2} aria-hidden='true' />
                      <ExpandMoreRoundedIcon fontSize='small' aria-hidden='true' />
                    </Button>
                  </Box>
                ) : null}
              </Box>
            </Box>
            <Box
              sx={{
                minWidth: 0,
                // Keep the search useful while leaving enough room for the
                // complete semantic groups at ordinary desktop widths.
                width: { xs: '100%', md: 200, lg: 210, xl: 220 },
                display: 'flex',
                alignItems: 'center',
                pl: { xs: 1, md: 0.75 },
              }}
            >
              <InstrumentSearch compact />
            </Box>
          </Box>
        </Toolbar>
      </Container>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: { xs: 'min(92vw, 420px)', sm: 420 },
              maxHeight: 'calc(100vh - 96px)',
              overflowY: 'auto',
              py: 0.5,
            },
          },
        }}
      >
        {overflowGroups.length > 0
          ? overflowGroups.flatMap((group) => {
              const GroupIcon = group.icon;
              const linkItems = group.links.map((link) => (
                <MenuItem
                  key={`${group.title}-${link.url}`}
                  component={Link}
                  href={link.url}
                  onClick={closeMenu}
                  sx={{
                    alignItems: 'flex-start',
                    mx: 1,
                    px: 1.5,
                    py: 0.5,
                    minHeight: 0,
                    borderRadius: 1,
                    transition: 'background-color 0.18s ease',
                    '&:hover': {
                      backgroundColor: 'hsl(var(--primary) / 0.14)',
                    },
                  }}
                >
                  <Box>
                    <Typography
                      variant='body2'
                      fontWeight={600}
                      sx={{
                        lineHeight: 1.2,
                        transition: 'color 0.18s ease',
                        '.MuiMenuItem-root:hover &': {
                          color: 'hsl(var(--primary))',
                        },
                      }}
                    >
                      <NavigationLinkLabel link={link} wrap />
                    </Typography>
                    {link.description ? (
                      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', lineHeight: 1.2 }}>
                        {link.description}
                      </Typography>
                    ) : null}
                  </Box>
                </MenuItem>
              ));

              return [
                <Box key={`group-heading-${group.title}`}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mx: 1,
                      px: 1.25,
                      py: 0.45,
                      borderRadius: 1,
                      color: 'hsl(var(--primary))',
                      backgroundColor: 'hsl(var(--primary) / .07)',
                    }}
                  >
                    <GroupIcon size={15} strokeWidth={2.2} aria-hidden='true' />
                    <Typography
                      variant='overline'
                      sx={{
                        fontWeight: 850,
                        fontSize: '0.7rem',
                        letterSpacing: '0.08em',
                      }}
                    >
                      {group.title}
                    </Typography>
                  </Box>
                </Box>,
                <Box key={`group-links-${group.title}`} sx={{ display: 'grid' }}>
                  {linkItems}
                </Box>,
              ];
            })
          : null}
        {overflowGroups.length > 0 && filteredSections.length > 0 ? <Divider sx={{ my: 1 }} /> : null}
        {filteredSections.length > 0
          ? filteredSections.flatMap((section, index) => {
              const SectionIcon = section.navigationIcon ? navigationIcons[section.navigationIcon] : MoreHorizontal;

              return [
                <Box
                  key={`secondary-heading-${section.title}`}
                  sx={{
                    px: 0,
                    pt: index === 0 && overflowGroups.length > 0 ? 0.5 : 1.25,
                    pb: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mx: 1,
                      px: 1.25,
                      py: 0.45,
                      borderRadius: 1,
                      color: 'hsl(var(--primary))',
                      backgroundColor: 'hsl(var(--primary) / .07)',
                    }}
                  >
                    <SectionIcon size={15} strokeWidth={2.2} aria-hidden='true' />
                    <Typography
                      variant='overline'
                      sx={{
                        fontWeight: 850,
                        fontSize: '0.7rem',
                        letterSpacing: '0.09em',
                      }}
                    >
                      {section.title === 'Mais' ? 'Mais opções' : section.title}
                    </Typography>
                  </Box>
                </Box>,
                ...section.links.map((link) => (
                  <MenuItem
                    key={`${section.title}-${link.url}`}
                    component={Link}
                    href={link.url}
                    onClick={closeMenu}
                    sx={{
                      alignItems: 'flex-start',
                      mx: 1,
                      px: 1.5,
                      py: 0.5,
                      minHeight: 0,
                      borderRadius: 1,
                      transition: 'background-color 0.18s ease',
                      '&:hover': {
                        backgroundColor: 'hsl(var(--primary) / 0.14)',
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                          lineHeight: 1.2,
                          transition: 'color 0.18s ease',
                          '.MuiMenuItem-root:hover &': {
                            color: 'hsl(var(--primary))',
                          },
                        }}
                      >
                        <NavigationLinkLabel link={link} wrap />
                      </Typography>
                      {link.description ? (
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', lineHeight: 1.2 }}>
                          {link.description}
                        </Typography>
                      ) : null}
                    </Box>
                  </MenuItem>
                )),
              ];
            })
          : null}
      </Menu>
      <Box
        sx={{
          borderTop: '1px solid hsl(var(--border))',
          backgroundColor: (theme) => (theme.palette.mode === 'light' ? '#414858' : 'hsl(var(--nav-subtle))'),
          color: (theme) => (theme.palette.mode === 'light' ? '#f4f5f7' : 'hsl(var(--nav-foreground))'),
          py: 0.75,
        }}
      >
        <Container maxWidth='xl' sx={{ px: { xs: 2, md: 3 } }}>
          <AppBreadcrumbs />
        </Container>
      </Box>
    </AppBar>
  );
};
